import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import { ErrorAlert } from '@bibliocommons/alerts';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { bindAll } from '@bibliocommons/utils-react';
import CharacterCount from '@bibliocommons/base-character-count';
import { getValidHtmlAttributes } from '@bibliocommons/utils-html';

import './TextField.scss';

export class TextField extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleChange(event) {
    if (this.props.onChange) {
      this.props.onChange(event.target.value);
    }
  }

  getAriaDescribedBy() {
    const describedByIds = [];

    if (this.props.errorMessage) {
      describedByIds.push(`${this.props.id}-error`);
    }

    if (this.props.info) {
      describedByIds.push(`${this.props.id}-info`);
    }

    if (this.props.maxLength || this.props.suggestedMaxLength) {
      describedByIds.push(`${this.props.id}-character-count`);
    }

    return describedByIds.length > 0 ? describedByIds.join(' ') : undefined;
  }

  renderCharacterCount() {
    const { value, suggestedMaxLength, maxLength, characterCountSrOnly, id } = this.props;
    if (suggestedMaxLength || maxLength) {
      return (
        <div id={`${id}-character-count`}>
          <CharacterCount
            screenReaderOnly={characterCountSrOnly}
            count={value ? value.length : 0}
            limit={suggestedMaxLength || maxLength}
          />
        </div>
      );
    }

    return null;
  }

  renderInfo() {
    return this.props.info ? (
      <div className="text-field__info" id={`${this.props.id}-info`}>
        {this.props.info}
      </div>
    ) : null;
  }

  render() {
    const hasError = Boolean(this.props.errorMessage);

    return (
      <div className={cn('cp-text-field', this.props.className)} data-key={this.props.dataKey}>
        {this.props.hideLabel ? (
          <ScreenReaderMessage>{this.props.label}</ScreenReaderMessage>
        ) : (
          <label className="text-field__label" htmlFor={this.props.id}>
            {this.props.label}
            {this.props.optional && (
              <span className="text-field__optional">
                (<FormattedMessage id="optional" />)
              </span>
            )}
          </label>
        )}
        <div className="input-holder">
          <div className={cn('text-field__border', { 'text-field__border--invalid': hasError })}>
            <input
              {...getValidHtmlAttributes(this.props, 'input')}
              onChange={this.handleChange}
              id={this.props.id}
              className={cn('text-field__input', {
                [`text-field__input--${this.props.size}`]: this.props.size,
                'text-field__input--icon': Boolean(this.props.inputIcon)
              })}
              ref={this.props.forwardedRef}
              data-key="text-field"
              aria-invalid={hasError}
              aria-describedby={this.getAriaDescribedBy()}
            />
            {this.props.inputIcon}
          </div>
        </div>
        {this.props.errorMessage && (
          <div id={`${this.props.id}-error`}>
            <ErrorAlert>{this.props.errorMessage}</ErrorAlert>
          </div>
        )}
        {this.renderInfo()}
        {this.renderCharacterCount()}
      </div>
    );
  }
}

TextField.defaultProps = {
  hideLabel: false,
  inputIcon: null
};

TextField.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  hideLabel: PropTypes.bool,
  id: PropTypes.string.isRequired,
  info: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  maxLength: PropTypes.number,
  suggestedMaxLength: PropTypes.number,
  value: PropTypes.string,
  characterCountSrOnly: PropTypes.bool,
  inputIcon: PropTypes.element,
  optional: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]).isRequired,
  errorMessage: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.string]),
  size: PropTypes.oneOf(['small', 'large', 'extra-large']),
  dataKey: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};

const WrappedTextField = React.forwardRef((props, ref) => {
  return <TextField {...props} forwardedRef={ref} />;
});

export default WrappedTextField;
